import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCalendarEvent,
  IconFileCheck,
  IconFlask2,
  IconMail,
  IconMapPin,
  IconPhone,
  IconShieldCheck,
  IconStethoscope,
  IconUserHeart,
} from "@tabler/icons-react";
import { NextGenBrand } from "@evolys/ui";
import type { PublicMarketingSiteDto } from "@evolys/types";
import { ServiceSwitcher } from "./ServiceSwitcher";

type MedicalLandingProps = {
  site: PublicMarketingSiteDto;
  style: React.CSSProperties;
};

const patientServices = [
  {
    icon: IconStethoscope,
    title: "Doctor visits",
    text: "Planned consultations in a private, professionally equipped clinical setting.",
  },
  {
    icon: IconFlask2,
    title: "Connected laboratory care",
    text: "A direct path to NextGen Diagnostics when your clinician requests laboratory testing.",
  },
  {
    icon: IconFileCheck,
    title: "Clear next steps",
    text: "Structured visit records, prescriptions, referrals, and follow-up information designed for continuity.",
  },
];

const clinicianSupport = [
  "Furnished consultation and examination rooms",
  "Flexible sessions for in-house and visiting doctors",
  "Clinical, administrative, inventory, and billing support",
];

export function MedicalLanding({ site, style }: MedicalLandingProps) {
  const profile = site.profile;
  const primaryLocation =
    site.locations.find((location) => location.isPrimary) ?? site.locations[0];

  return (
    <Box className="site medical-site" style={style}>
      <header className="site-header medical-header">
        <Container size="xl">
          <Group
            className="site-header-row"
            justify="space-between"
            py="md"
            wrap="nowrap"
          >
            <Box className="site-header-brand">
              <NextGenBrand
                name="NextGen Medical"
                subtitle="Doctor visits, thoughtfully supported"
                imageSize={46}
              />
            </Box>
            <Group gap="xl" visibleFrom="sm">
              <Anchor href="#medical-services">Patient care</Anchor>
              <Anchor href="#medical-clinicians">For doctors</Anchor>
              <Anchor href="#medical-contact">Contact</Anchor>
              <Button
                component="a"
                href="#medical-contact"
                leftSection={<IconCalendarEvent size={17} />}
              >
                Appointments coming soon
              </Button>
            </Group>
            <Button
              component="a"
              href="#medical-contact"
              size="sm"
              hiddenFrom="sm"
              aria-label="Contact NextGen Medical about a doctor visit"
            >
              Contact
            </Button>
          </Group>
        </Container>
        <ServiceSwitcher active="medical" />
      </header>

      <main>
        <section className="medical-hero">
          <div className="medical-chart-grid" aria-hidden="true" />
          <Container className="medical-hero-container" size="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={80}>
              <Stack className="medical-hero-copy" justify="center" gap="xl">
                <Badge className="medical-launch-badge" variant="light" size="lg">
                  NextGen Medical · Opening soon
                </Badge>
                <div>
                  <Text className="eyebrow">Care that keeps its context</Text>
                  <Title order={1}>A calmer, more connected way to see your doctor.</Title>
                </div>
                <Text className="medical-hero-body">
                  NextGen Medical is preparing private, fully equipped consultation rooms for
                  scheduled patient visits with in-house and visiting doctors—supported by secure
                  records and a direct connection to NextGen Diagnostics.
                </Text>
                <Group className="hero-actions">
                  <Button
                    component="a"
                    href="#medical-contact"
                    size="lg"
                    rightSection={<IconArrowRight size={18} />}
                  >
                    Ask about a visit
                  </Button>
                  <Button component="a" href="#medical-experience" size="lg" variant="subtle">
                    See what to expect
                  </Button>
                </Group>
                <Text className="medical-safety-note">
                  Planned outpatient visits only. This is not an emergency service.
                </Text>
              </Stack>

              <Box className="visit-pathway" aria-label="A connected NextGen Medical visit">
                <div className="visit-record" aria-hidden="true">
                  <span className="visit-record-rule" />
                  <span className="visit-record-rule" />
                  <span className="visit-record-rule visit-record-rule-short" />
                </div>
                <Paper className="visit-pathway-card visit-pathway-arrive">
                  <IconUserHeart size={25} />
                  <div>
                    <Text fw={800}>Arrive prepared</Text>
                    <Text size="sm">Your details and reason for visiting stay organized.</Text>
                  </div>
                </Paper>
                <Paper className="visit-pathway-card visit-pathway-consult">
                  <IconStethoscope size={25} />
                  <div>
                    <Text fw={800}>See your doctor</Text>
                    <Text size="sm">A private visit in a properly equipped clinical room.</Text>
                  </div>
                </Paper>
                <Paper className="visit-pathway-card visit-pathway-followup">
                  <IconFileCheck size={25} />
                  <div>
                    <Text fw={800}>Leave with clarity</Text>
                    <Text size="sm">Prescriptions, requests, referrals, and follow-up in context.</Text>
                  </div>
                </Paper>
              </Box>
            </SimpleGrid>
          </Container>
        </section>

        <section className="medical-services" id="medical-services">
          <Container size="xl">
            <Text className="section-label">For patients</Text>
            <Title order={2}>The visit, the tests, and the next step—designed to connect.</Title>
            <Text className="section-intro">
              The first release will focus on scheduled consultations and dependable follow-up,
              without asking patients to carry the burden of reconnecting every part of their care.
            </Text>
            <SimpleGrid cols={{ base: 1, md: 3 }} mt={48}>
              {patientServices.map(({ icon: Icon, title, text }) => (
                <Paper className="medical-service-card" key={title}>
                  <span className="medical-service-icon" aria-hidden="true">
                    <Icon size={25} stroke={1.8} />
                  </span>
                  <Title order={3}>{title}</Title>
                  <Text>{text}</Text>
                </Paper>
              ))}
            </SimpleGrid>
          </Container>
        </section>

        <section className="medical-experience" id="medical-experience">
          <Container size="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={70}>
              <Box>
                <Text className="section-label">Designed for continuity</Text>
                <Title order={2}>A useful record, not just another appointment.</Title>
              </Box>
              <Stack gap="lg" className="medical-experience-copy">
                <Text>
                  Each visit will be organized around a readable clinical summary, so the next
                  physician can understand what happened, what changed, and what should happen next.
                </Text>
                <div className="medical-feature-list">
                  {[
                    "Structured histories, examination findings, vitals, and care plans",
                    "Digital prescriptions, investigation requests, and referral summaries",
                    "Secure access controls and traceable handling of sensitive health information",
                  ].map((item) => (
                    <div key={item}>
                      <IconShieldCheck size={19} aria-hidden="true" />
                      <Text>{item}</Text>
                    </div>
                  ))}
                </div>
              </Stack>
            </SimpleGrid>
          </Container>
        </section>

        <section className="medical-clinicians" id="medical-clinicians">
          <Container size="xl">
            <Paper className="clinician-suite">
              <SimpleGrid cols={{ base: 1, md: 2 }} spacing={60}>
                <Stack gap="lg">
                  <Text className="section-label">For doctors</Text>
                  <Title order={2}>Bring your practice. We prepare the room around it.</Title>
                  <Text>
                    NextGen Medical will offer flexible, resourced clinical space for established
                    and visiting doctors who need a professional setting without building a full
                    office operation from scratch.
                  </Text>
                  <Button
                    component="a"
                    href={`mailto:${profile.contactEmail ?? "support@nextgenja.com"}?subject=NextGen%20Medical%20clinical%20space`}
                    variant="white"
                    rightSection={<IconArrowRight size={18} />}
                  >
                    Discuss clinical space
                  </Button>
                </Stack>
                <div className="clinician-support-list">
                  {clinicianSupport.map((item, index) => (
                    <div key={item}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <Text>{item}</Text>
                    </div>
                  ))}
                </div>
              </SimpleGrid>
            </Paper>
          </Container>
        </section>

        <section className="medical-contact" id="medical-contact">
          <Container size="xl">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing={60}>
              <Box>
                <Text className="section-label">Opening soon</Text>
                <Title order={2}>Medical appointment booking will open with our clinical system.</Title>
                <Text className="section-intro">
                  Until then, contact the NextGen team with questions about patient visits,
                  visiting-doctor sessions, or the launch.
                </Text>
              </Box>
              <Paper className="medical-contact-card">
                {profile.contactPhone ? (
                  <Anchor href={`tel:${profile.contactPhone}`}>
                    <IconPhone size={21} />
                    <span>
                      <small>Call</small>
                      <strong>{profile.contactPhone}</strong>
                    </span>
                  </Anchor>
                ) : null}
                {profile.contactEmail ? (
                  <Anchor href={`mailto:${profile.contactEmail}`}>
                    <IconMail size={21} />
                    <span>
                      <small>Email</small>
                      <strong>{profile.contactEmail}</strong>
                    </span>
                  </Anchor>
                ) : null}
                {primaryLocation?.address ? (
                  <div>
                    <IconMapPin size={21} />
                    <span>
                      <small>Location</small>
                      <strong>{primaryLocation.address}</strong>
                    </span>
                  </div>
                ) : null}
              </Paper>
            </SimpleGrid>
          </Container>
        </section>
      </main>

      <footer>
        <Container size="xl">
          <Group className="site-footer-row" justify="space-between" py="xl">
            <NextGenBrand name="NextGen Medical" compact subtitle="Part of the NextGen care experience" />
            <Text size="sm" c="dimmed">
              © {new Date().getFullYear()} {site.laboratoryName}
            </Text>
          </Group>
        </Container>
      </footer>
    </Box>
  );
}
